import type { BBox, Dimension, Vec3 } from "@cfg/kernel";
import { Html, Line } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { useCallback, useMemo, useRef } from "react";
import * as THREE from "three";
import { prefersReducedMotion } from "../timing.ts";
import { useLabelCollisions } from "./collisions.ts";
import { arrowSize, dimensionText, drawDimension, type DimensionDrawing } from "./dimensions.ts";

export interface DimensionItem {
  dim: Dimension;
  label: string;
  value: unknown;
  unit?: string;
  /** Off items stay mounted and fade out, so switching the view on and off animates. */
  shown: boolean;
}

export interface DimensionColors {
  line: string;
  active: string;
}

/** A linear dimension seen this close to end-on (|cos| of line vs view direction) is hidden. */
const END_ON = 0.96;
/** Fade in / out when a dimension is switched on or off, eased at both ends. */
const FADE_S = 0.28;

const flat = (segs: [Vec3, Vec3][]) => segs.flat();
const smoothstep = (p: number) => p * p * (3 - 2 * p);

type Fading = { p: number; guide: number; line: number };
type LineObj = { material: { opacity: number } };

/**
 * Dimension lines (cotas) over the model: the line with arrowheads, faint extension lines /
 * circle / angle arms, and a value label that focuses the param's field when clicked. Drawn
 * without depth test so the part never hides them. All of them stay faint (`.is-muted` labels)
 * except the selected param's, which use `colors.active`. Items with `shown: false` fade out
 * (and back in) instead of unmounting.
 */
export function Dimensions({ bbox, items, active, dragging, colors, onPick }: { bbox: BBox; items: DimensionItem[]; active: string | null; dragging: string | null; colors: DimensionColors; onPick: (name: string) => void }) {
  const size = arrowSize(bbox);
  const drawings = useMemo(() => items.map((it) => drawDimension(it.dim, size)), [items, size]);
  // Keys that survive reordering, so labels don't remount and blink.
  const keys = useMemo(() => {
    const seen = new Map<string, number>();
    return items.map((it) => {
      const n = seen.get(it.dim.param) ?? 0;
      seen.set(it.dim.param, n + 1);
      return `${it.dim.param}#${n}`;
    });
  }, [items]);
  // Elements by key, not index: each label renders in its own React root, so a reordered sibling's
  // detach can land after another's attach; keyed slots never clear someone else's.
  const labelEls = useRef(new Map<string, HTMLElement>());
  const groupEls = useRef(new Map<string, THREE.Group>());
  const guideEls = useRef(new Map<string, LineObj>());
  const lineEls = useRef(new Map<string, LineObj>());
  const fades = useRef(new Map<string, Fading>());
  const keysRef = useRef(keys);
  keysRef.current = keys;
  const labelsInOrder = useCallback(() => keysRef.current.map((k) => labelEls.current.get(k) ?? null), []);
  // Overlapping labels: the selected param's win, then the order drawn.
  const selected = useRef<string[]>([]);
  selected.current = [active, dragging].filter((p): p is string => !!p);
  const labelsByPriority = useCallback(() => {
    const ks = keysRef.current;
    const first = ks.filter((k) => selected.current.includes(k.slice(0, k.lastIndexOf("#"))));
    return [...first, ...ks.filter((k) => !first.includes(k))].map((k) => labelEls.current.get(k) ?? null);
  }, []);
  useLabelCollisions(labelsByPriority, items.length);
  const endOn = useEndOn(drawings);
  const shown = useRef<boolean[]>([]);
  shown.current = items.map((it) => it.shown);
  const reduced = useMemo(() => prefersReducedMotion(), []);

  useFrame((_, delta) => {
    const ks = keysRef.current;
    ks.forEach((k, i) => {
      const f = fades.current.get(k);
      if (!f) return;
      const target = shown.current[i] ? 1 : 0;
      if (f.p !== target) f.p = reduced ? target : target > f.p ? Math.min(1, f.p + delta / FADE_S) : Math.max(0, f.p - delta / FADE_S);
      const a = smoothstep(f.p);
      const guide = guideEls.current.get(k);
      if (guide) guide.material.opacity = f.guide * a;
      const line = lineEls.current.get(k);
      if (line) line.material.opacity = f.line * a;
      const visible = a > 0.001 && !endOn.current[i];
      const g = groupEls.current.get(k);
      if (g && g.visible !== visible) g.visible = visible;
      const el = labelEls.current.get(k);
      const wrap = el?.parentElement;
      if (el && wrap) {
        wrap.style.opacity = String(a);
        // Off (or fading out) labels drop out of collisions and clicks; end-on ones hide outright.
        const off = !shown.current[i] || a < 0.001;
        if (off !== el.hasAttribute("data-off")) el.toggleAttribute("data-off", off);
        const edge = !!endOn.current[i];
        if (edge !== el.hasAttribute("data-edge-on")) el.toggleAttribute("data-edge-on", edge);
        wrap.style.visibility = a < 0.001 || edge ? "hidden" : "";
      }
    });
  });

  return (
    <>
      {items.map((it, i) => {
        const d = drawings[i]!;
        const k = keys[i]!;
        const on = active === it.dim.param || dragging === it.dim.param;
        const color = on ? colors.active : colors.line;
        const base = { guide: on ? 0.8 : 0.25, line: on ? 1 : 0.35 };
        const f = fades.current.get(k);
        if (f) Object.assign(f, base);
        else fades.current.set(k, { p: 0, ...base });
        return (
          <group
            key={k}
            ref={(g) => {
              if (g) groupEls.current.set(k, g);
              else if (groupEls.current.get(k)?.parent == null) groupEls.current.delete(k);
            }}
            visible={false}
            renderOrder={on ? 12 : 11}
          >
            {d.guides.length > 0 && (
              <Line
                ref={(l) => void (l ? guideEls.current.set(k, l as unknown as LineObj) : guideEls.current.delete(k))}
                points={flat(d.guides)}
                segments
                color={color}
                lineWidth={1}
                transparent
                opacity={0}
                depthTest={false}
              />
            )}
            {d.lines.length > 0 && (
              <Line
                ref={(l) => void (l ? lineEls.current.set(k, l as unknown as LineObj) : lineEls.current.delete(k))}
                points={flat(d.lines)}
                segments
                color={color}
                lineWidth={on ? 2.5 : 1.75}
                transparent
                opacity={0}
                depthTest={false}
              />
            )}
            <Html position={d.label} center zIndexRange={[on ? 10 + items.length + 1 : 10 + items.length - i, 0]} style={{ pointerEvents: "auto", opacity: 0, visibility: "hidden" }}>
              <button
                type="button"
                ref={(el) => {
                  if (el) labelEls.current.set(k, el);
                  else if (!labelEls.current.get(k)?.isConnected) labelEls.current.delete(k);
                }}
                className={`cfg-float cfg-float--dim${on ? " is-active" : " is-muted"}${dragging === it.dim.param ? " is-dragging" : ""}`}
                data-param={it.dim.param}
                tabIndex={-1}
                onClick={() => onPick(it.dim.param)}
                title={it.label}
              >
                <span className="cfg-float-label">{it.label}</span>
                <span className="cfg-float-value">{dimensionText(it.dim.kind, it.value, it.unit)}</span>
              </button>
            </Html>
          </group>
        );
      })}
    </>
  );
}

/** Which linear dimensions the camera looks along (line and label hide), checked ~10×/s. */
function useEndOn(drawings: DimensionDrawing[]) {
  const last = useRef(0);
  const flags = useRef<boolean[]>([]);
  const view = useMemo(() => new THREE.Vector3(), []);
  const dir = useMemo(() => new THREE.Vector3(), []);
  useFrame(({ camera, clock }) => {
    const t = clock.elapsedTime;
    if (t - last.current < 0.1 && flags.current.length === drawings.length) return;
    last.current = t;
    camera.getWorldDirection(view);
    flags.current = drawings.map((d) => {
      if (d.kind !== "linear" || !d.lines[0]) return false;
      const [a, b] = d.lines[0];
      dir.set(b[0] - a[0], b[1] - a[1], b[2] - a[2]).normalize();
      return Math.abs(dir.dot(view)) > END_ON;
    });
  });
  return flags;
}
